import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    Avatar,
    Button,
    DataTable,
    DropdownMenu,
    InputTextArea,
    ModalFeedback,
    PopoverMenu,
    SparkIcon,
    SparkModalPortal,
    TableToolbarContent,
    TagColored,
    TagGeneral,
    TruncatedText,
    getOverlayPortalParent,
    sparkToast,
    useAnchoredPanelPosition,
    useEscapeKey,
    useOutsideClick,
} from '@spark';
import SettingsSectionCard from '@/features/settings/components/SettingsSectionCard.jsx';
import { formatDateTime } from '@/shared/utils/helpers';
import { fetchCreatorRequests, setCreatorRequestStatus } from '../api/waitlistApi';
import { WAITLIST_ANSWER_LABELS } from '../utils/waitlistOptions';
import '@/features/settings/components/settings.css';
import '../styles/waitlist.css';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

/** Status filter options; the first one is the default view for gods. */
export const WAITLIST_STATUS_FILTERS = [
    { id: 'pending', label: 'Pending' },
    { id: 'approved', label: 'Approved' },
    { id: 'rejected', label: 'Rejected' },
    { id: 'all', label: 'All requests' },
];

/** `TagColored` variant per request status (approvals whose pre-authorisation lapsed render grey). */
const STATUS_TAG = {
    pending: { label: 'Pending', variant: 'yellow' },
    approved: { label: 'Approved', variant: 'lime' },
    rejected: { label: 'Rejected', variant: 'volcano' },
    expired: { label: 'Expired', variant: 'disabled' },
};

/**
 * Row-menu entries per displayed status: every status a god can move the
 * request to from where it is now.  `expired` is an approval whose unused
 * pre-authorisation lapsed, so approving again re-issues it.
 */
const STATUS_ACTIONS = {
    pending: ['approved', 'rejected'],
    approved: ['rejected', 'pending'],
    rejected: ['approved', 'pending'],
    expired: ['approved', 'rejected', 'pending'],
};

const ACTION_MENU_ITEMS = {
    approved: { label: 'Approve request', prefixIconSlug: 'check' },
    rejected: { label: 'Reject request', prefixIconSlug: 'close' },
    pending: { label: 'Set to pending', prefixIconSlug: 'arrow-back-up' },
};

const EMPTY_CELL = '—';

/** The note is stored on the request and included in the decision email sent to the applicant. */
export const DECISION_NOTE_PLACEHOLDER = 'Sent to the applicant with the decision email';

/** Column widths: every text column has a floor so the table scrolls sideways instead of clipping cells. */
const COLUMN_MIN_WIDTH = {
    applicant: 240,
    role: 160,
    industry: 200,
    company_size: 130,
    heard_about: 180,
    reason: 200,
};

function getEmailLocalPart(email) {
    return (email || '').split('@')[0] || '';
}

/** Display status: an approval whose pre-authorisation was never used and has lapsed shows as `expired`. */
export function statusOf(request) {
    if (request.status === 'approved' && request.expired && !request.fulfilled_at) return 'expired';
    return request.status;
}

function renderAnswer(value) {
    const text = typeof value === 'string' ? value.trim() : '';
    if (!text) return EMPTY_CELL;
    return <TruncatedText text={text} tooltipText={text} maxWidth="100%" />;
}

/**
 * Copy for the confirmation modal of a `from` → `to` status change.  The
 * modal renders `<b>{email}</b>{subject} {body}`, so `subject` attaches
 * directly to the email (e.g. "'s request") and `body` follows it.
 */
export function describeTransition(request, from, to) {
    const email = request.email || 'the applicant';
    const wasApproved = from === 'approved' || from === 'expired';
    const membershipsKept = 'Workspaces they already created and their memberships are kept.';
    if (to === 'approved') {
        return {
            title: 'Approve Creator request?',
            subject: '',
            body: 'will get the Creator platform role and can create workspaces the next time they sign in.',
            feedbackType: 'info',
            danger: false,
            primaryLabel: 'Approve',
            withNote: true,
            toast: `${email} was approved and can now create workspaces`,
        };
    }
    if (to === 'rejected') {
        return {
            title: wasApproved ? 'Revoke approval?' : 'Reject Creator request?',
            subject: '',
            body: wasApproved
                ? `will lose the Creator platform role and can no longer create workspaces. ${membershipsKept}`
                : 'will not be granted access. They can file a new request after the cooldown.',
            feedbackType: 'warning',
            danger: true,
            primaryLabel: wasApproved ? 'Revoke' : 'Reject',
            withNote: true,
            toast: wasApproved ? `${email}'s approval was revoked` : `${email}'s request was rejected`,
        };
    }
    return {
        title: 'Move request back to pending?',
        subject: "'s request",
        body: `returns to the review queue without a decision and no email is sent.${
            wasApproved ? ` They lose the Creator platform role until it is approved again. ${membershipsKept}` : ''
        }`,
        feedbackType: 'info',
        danger: false,
        primaryLabel: 'Set to pending',
        withNote: false,
        toast: `${email}'s request is pending again`,
    };
}

function RequestRowActions({ actions, onSelect }) {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);
    const panelRef = useRef(null);
    const close = () => setOpen(false);

    useOutsideClick([wrapRef, panelRef], close, open);
    useEscapeKey(close, open);
    const panelStyle = useAnchoredPanelPosition(wrapRef, panelRef, open, 'end');

    const panel = open ? (
        <div
            ref={panelRef}
            className="settings-users__row-actions-panel"
            style={panelStyle}
        >
            <PopoverMenu
                tip={false}
                items={actions.map((target) => ({
                    ...ACTION_MENU_ITEMS[target],
                    showPrefixIcon: true,
                    onClick: () => {
                        setOpen(false);
                        onSelect?.(target);
                    },
                }))}
            />
        </div>
    ) : null;

    return (
        <div ref={wrapRef} className="settings-users__row-actions">
            <Button
                variant="ghost"
                size="small"
                aria-label="Request actions"
                icon={<SparkIcon slug="dots" size="small" />}
                onClick={(e) => {
                    e.stopPropagation();
                    setOpen((prev) => !prev);
                }}
            />
            {open ? createPortal(panel, getOverlayPortalParent(wrapRef.current)) : null}
        </div>
    );
}

function StatusFilter({ value, onChange }) {
    const [open, setOpen] = useState(false);
    const current = WAITLIST_STATUS_FILTERS.find((item) => item.id === value) || WAITLIST_STATUS_FILTERS[0];

    return (
        <div className="settings-users__filters">
            <DropdownMenu
                className="settings-users__type-filter"
                showLabel={false}
                placeholder=""
                text=""
                value={current.label}
                open={open}
                onOpenChange={setOpen}
                prefixIcon={(
                    <>
                        <SparkIcon slug="filter" size="small" />
                        <span className="settings-users__type-label">Status</span>
                        <TagGeneral label={current.label} />
                    </>
                )}
                items={WAITLIST_STATUS_FILTERS.map((item) => ({ label: item.label }))}
                onSelect={(item) => {
                    const next = WAITLIST_STATUS_FILTERS.find((option) => option.label === item.label);
                    onChange?.(next ? next.id : WAITLIST_STATUS_FILTERS[0].id);
                    setOpen(false);
                }}
            />
        </div>
    );
}

/**
 * God-only review queue for the public waiting list.
 *
 * Lists Creator requests (`GET /platform/creator-requests`) together with the
 * four questionnaire answers the applicant picked, and lets a god move each
 * request to any other status — approve or reject pending ones, revoke an
 * approval, reconsider a rejection or send a request back to the queue —
 * through `POST /platform/creator-requests/{id}/status`. Mirrors the User
 * Management section so the two review screens look alike.
 *
 * `onRequestsChanged` fires after every successful status change so the
 * parent can refresh anything derived from the queue (the `Waiting List (N)`
 * sidebar count in Settings); a refused change leaves it untouched.
 */
export default function WaitingListManagement({ onRequestsChanged }) {
    const [statusFilter, setStatusFilter] = useState(WAITLIST_STATUS_FILTERS[0].id);
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [decision, setDecision] = useState(null); // { request, from, to }
    const [note, setNote] = useState('');
    const [deciding, setDeciding] = useState(false);

    const load = useCallback(async (status) => {
        setLoading(true);
        setLoadError('');
        try {
            const rows = await fetchCreatorRequests(status);
            setRequests(rows);
        } catch (error) {
            setRequests([]);
            setLoadError(error?.message || 'Failed to load the waiting list');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load(statusFilter);
    }, [statusFilter, load]);

    useEffect(() => {
        setPage(1);
    }, [statusFilter, pageSize]);

    const pagedRequests = useMemo(
        () => requests.slice((page - 1) * pageSize, page * pageSize),
        [requests, page, pageSize]
    );

    const openDecision = (request, to) => {
        setNote('');
        setDecision({ request, from: statusOf(request), to });
    };

    const closeDecision = () => {
        if (deciding) return;
        setDecision(null);
        setNote('');
    };

    const transition = decision ? describeTransition(decision.request, decision.from, decision.to) : null;

    const confirmDecision = async () => {
        if (!decision || deciding) return;
        setDeciding(true);
        const { request, to } = decision;
        const result = await setCreatorRequestStatus(request._id, to, transition.withNote ? note : null);
        setDeciding(false);
        if (!result.ok) {
            sparkToast.error(result.error);
            return;
        }
        sparkToast.success(transition.toast);
        setDecision(null);
        setNote('');
        load(statusFilter);
        onRequestsChanged?.();
    };

    const columns = [
        {
            title: 'Applicant',
            key: 'applicant',
            dataIndex: 'email',
            minWidth: COLUMN_MIN_WIDTH.applicant,
            render: (_, request) => {
                const displayName = request.name || getEmailLocalPart(request.email);
                return (
                    <Avatar
                        size="medium"
                        type="initials"
                        alt={displayName}
                        primaryText={displayName}
                        secondaryText={request.email || ''}
                        showContent
                    />
                );
            },
        },
        {
            title: WAITLIST_ANSWER_LABELS.role,
            key: 'role',
            dataIndex: 'role',
            minWidth: COLUMN_MIN_WIDTH.role,
            render: renderAnswer,
        },
        {
            title: WAITLIST_ANSWER_LABELS.industry,
            key: 'industry',
            dataIndex: 'industry',
            minWidth: COLUMN_MIN_WIDTH.industry,
            render: renderAnswer,
        },
        {
            title: WAITLIST_ANSWER_LABELS.company_size,
            key: 'company_size',
            dataIndex: 'company_size',
            minWidth: COLUMN_MIN_WIDTH.company_size,
            render: renderAnswer,
        },
        {
            title: WAITLIST_ANSWER_LABELS.heard_about,
            key: 'heard_about',
            dataIndex: 'heard_about',
            minWidth: COLUMN_MIN_WIDTH.heard_about,
            render: renderAnswer,
        },
        {
            title: 'Reason',
            key: 'reason',
            dataIndex: 'reason',
            // Grows into leftover width on wide screens; the floor keeps it readable when the table scrolls.
            flex: 1,
            minWidth: COLUMN_MIN_WIDTH.reason,
            render: renderAnswer,
        },
        {
            title: 'Submitted',
            key: 'created_at',
            dataIndex: 'created_at',
            width: 170,
            render: (value) => (value ? formatDateTime(value) : EMPTY_CELL),
        },
        {
            title: 'Status',
            key: 'status',
            dataIndex: 'status',
            width: 120,
            render: (_, request) => {
                const tag = STATUS_TAG[statusOf(request)] || STATUS_TAG.pending;
                return <TagColored label={tag.label} variant={tag.variant} />;
            },
        },
        {
            title: '',
            key: 'actions',
            width: 56,
            render: (_, request) => {
                const actions = STATUS_ACTIONS[statusOf(request)];
                return actions ? (
                    <RequestRowActions actions={actions} onSelect={(to) => openDecision(request, to)} />
                ) : null;
            },
        },
    ];

    const emptyText = loading
        ? 'Loading requests...'
        : (loadError || 'No requests found');

    return (
        <>
            <SettingsSectionCard
                iconSlug="user"
                title="Waiting List"
                headerExtra={(
                    <Button
                        variant="tertiary"
                        size="medium"
                        disabled={loading}
                        onClick={() => load(statusFilter)}
                    >
                        Refresh
                    </Button>
                )}
            >
                <div className="settings-users">
                    <StatusFilter value={statusFilter} onChange={setStatusFilter} />
                    <div className="settings-users__table-block">
                        <div className="settings-users__toolbar">
                            <TableToolbarContent
                                title={`Requests (${requests.length})`}
                                showInfoIconTitle={false}
                                showSubtitle={false}
                            />
                        </div>
                        <DataTable
                            rowKey={(record) => String(record._id || record.email)}
                            columns={columns}
                            dataSource={pagedRequests}
                            disableHoverBackground
                            pagination={{
                                current: page,
                                pageSize,
                                total: requests.length,
                                pageSizeOptions: PAGE_SIZE_OPTIONS,
                                onChange: setPage,
                                onPageSizeChange: (size) => {
                                    setPageSize(size);
                                    setPage(1);
                                },
                            }}
                            emptyText={emptyText}
                        />
                    </div>
                </div>
            </SettingsSectionCard>

            {decision ? (
                <SparkModalPortal open onClose={closeDecision} size="text">
                    <ModalFeedback
                        feedbackType={transition.feedbackType}
                        title={transition.title}
                        content={(
                            <div className="waitlist-decision-body">
                                <p className="spark-modal-text-content">
                                    <b>{decision.request.email || 'The applicant'}</b>
                                    {transition.subject}
                                    {' '}
                                    {transition.body}
                                </p>
                                {transition.withNote ? (
                                    <InputTextArea
                                        label="Note (optional)"
                                        placeholder={DECISION_NOTE_PLACEHOLDER}
                                        value={note}
                                        size="small"
                                        stretch
                                        maxChars={500}
                                        showCharCount
                                        disabled={deciding}
                                        onChange={(event) => setNote(event.target.value)}
                                    />
                                ) : null}
                            </div>
                        )}
                        showOverlay={false}
                        danger={transition.danger}
                        primaryLabel={transition.primaryLabel}
                        primaryDisabled={deciding}
                        tertiaryLabel="Cancel"
                        onPrimaryClick={confirmDecision}
                        onTertiaryClick={closeDecision}
                        onCloseClick={closeDecision}
                    />
                </SparkModalPortal>
            ) : null}
        </>
    );
}
